import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { FiCalendar } from 'react-icons/fi';
import WidgetHeader from './WidgetHeader';
import { format, addDays, isToday } from 'date-fns';
import type { Task } from '../../types/work';

interface ScheduledEvent {
  id: string;
  title: string;
  event_type: string;
  scheduled_at?: string;
  next_run_at?: string;
  is_recurring?: boolean;
}

interface CalendarPreviewWidgetProps {
  tasks: Task[];
  scheduledEvents: ScheduledEvent[];
}

const CalendarPreviewWidget: React.FC<CalendarPreviewWidgetProps> = ({ tasks, scheduledEvents }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);

  // Build 7-day strip
  const days = Array.from({ length: 7 }, (_, i) => addDays(now, i));

  // Count tasks per day
  const tasksByDay = (d: Date) => {
    const dateStr = format(d, 'yyyy-MM-dd');
    return tasks.filter(t =>
      t.due_date && t.due_date.slice(0, 10) === dateStr &&
      t.status !== 'completed' && t.status !== 'cancelled'
    );
  };

  // Build agenda: tasks + scheduled events for next 7 days, sorted by date/time
  type AgendaItem = { type: 'task'; task: Task; date: string } | { type: 'event'; event: ScheduledEvent; date: string };
  const agenda: AgendaItem[] = [];

  const weekEnd = addDays(now, 6).toISOString().slice(0, 10);
  const overdueFloor = addDays(now, -14).toISOString().slice(0, 10);
  tasks.forEach(t => {
    if (!t.due_date) return;
    const d = t.due_date.slice(0, 10);
    if (d >= todayStr && d <= weekEnd && t.status !== 'completed' && t.status !== 'cancelled') {
      agenda.push({ type: 'task', task: t, date: t.due_date });
    }
    // Overdue tasks (last 14 days only)
    if (d < todayStr && d >= overdueFloor && t.status !== 'completed' && t.status !== 'cancelled') {
      agenda.push({ type: 'task', task: t, date: t.due_date });
    }
  });

  scheduledEvents.forEach(e => {
    const ts = e.next_run_at || e.scheduled_at;
    if (!ts) return;
    const d = ts.slice(0, 10);
    if (d >= todayStr && d <= weekEnd) {
      agenda.push({ type: 'event', event: e, date: ts });
    }
  });

  agenda.sort((a, b) => a.date.localeCompare(b.date));
  const displayAgenda = agenda.slice(0, 8);
  const moreCount = agenda.length - 8;

  const STATUS_COLORS: Record<string, string> = {
    todo: darkMode ? 'text-gray-400' : 'text-gray-500',
    in_progress: 'text-violet-400',
    blocked: 'text-red-400',
    in_review: 'text-sky-400',
  };

  if (tasks.length === 0 && scheduledEvents.length === 0) {
    return (
      <div className={`rounded-lg border-l-4 border-amber-500 p-4 ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
        <WidgetHeader icon={<FiCalendar className="w-4 h-4 text-amber-400" />} title="Calendar" accentColor="amber" />
        <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Clear schedule ahead.
        </p>
        <button onClick={() => navigate('/calendar')} className="mt-2 text-xs text-amber-400 hover:text-amber-300 font-medium">
          Open Calendar &rarr;
        </button>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border-l-4 border-amber-500 p-4 ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
      <div className="flex items-center gap-2 mb-3">
        <FiCalendar className="w-4 h-4 text-amber-400" />
        <span className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Calendar</span>
      </div>

      {/* Week strip */}
      <div className="flex gap-1 mb-3 overflow-x-auto">
        {days.map(d => {
          const dayTasks = tasksByDay(d);
          const today = isToday(d);
          return (
            <button
              key={format(d, 'yyyy-MM-dd')}
              onClick={() => navigate('/calendar')}
              className={`flex-1 text-center rounded py-1 transition-colors ${
                today
                  ? darkMode ? 'bg-amber-900/30 border border-amber-500/50' : 'bg-amber-50 border border-amber-300'
                  : darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100'
              }`}
            >
              <div className={`text-[9px] font-medium ${today ? 'text-amber-400' : darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {format(d, 'EEE')}
              </div>
              <div className={`text-xs font-bold ${today ? 'text-amber-300' : darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {format(d, 'd')}
              </div>
              {dayTasks.length > 0 && (
                <div className="flex justify-center gap-0.5 mt-0.5">
                  {dayTasks.slice(0, 3).map((_, i) => (
                    <span key={i} className="w-1 h-1 rounded-full bg-amber-400" />
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Agenda list */}
      <div className="space-y-1">
        {displayAgenda.map((item, i) => (
          <div key={i} className={`flex items-center gap-2 text-xs py-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {item.type === 'task' ? (
              <>
                <span className={STATUS_COLORS[item.task.status] || 'text-gray-400'}>&#10003;</span>
                <button onClick={() => navigate('/tasks')} className="truncate hover:underline flex-1 text-left">
                  {item.task.title}
                </button>
                <span className={`text-[10px] shrink-0 ${
                  item.date.slice(0, 10) < todayStr ? 'text-red-400 font-medium' : darkMode ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  {item.date.slice(0, 10) < todayStr ? 'overdue' : format(new Date(item.date), 'EEE')}
                </span>
              </>
            ) : (
              <>
                <span className="text-amber-400">&#9670;</span>
                <span className="truncate flex-1">{item.event.title}</span>
                {item.event.is_recurring && <span className="text-[10px] text-gray-500">&#8635;</span>}
                <span className={`text-[10px] shrink-0 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                  {format(new Date(item.date), 'EEE HH:mm')}
                </span>
              </>
            )}
          </div>
        ))}
        {moreCount > 0 && (
          <p className={`text-[10px] ${darkMode ? 'text-amber-400/60' : 'text-amber-600/60'}`}>
            +{moreCount} more items
          </p>
        )}
      </div>

      <button onClick={() => navigate('/calendar')} className="mt-3 text-xs text-amber-400 hover:text-amber-300 font-medium">
        Open Calendar &rarr;
      </button>
    </div>
  );
};

export default CalendarPreviewWidget;
